import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { 
  Search, 
  Package, 
  DollarSign, 
  Hash, 
  CheckCircle,
  X,
  Target,
  Zap
} from "lucide-react";
import { InvoiceItem, Product } from "@/hooks/useWorkstationStore";
import { cn } from "@/lib/utils";

interface MatchingDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  invoiceItem: InvoiceItem | null;
  availableProducts: Product[];
  onMatch: (invoiceItemId: string, productId: string) => void;
  onCreateNewProduct?: (invoiceItem: InvoiceItem) => void;
}

export const MatchingDrawer = ({ 
  isOpen, 
  onClose, 
  invoiceItem, 
  availableProducts,
  onMatch,
  onCreateNewProduct 
}: MatchingDrawerProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);

  if (!invoiceItem) return null;

  // Filter and score products
  const scoredProducts = availableProducts
    .filter(product => !product.costPerUnit) // Only show products without costs
    .map(product => {
      let score = 0;
      let matchReasons: string[] = [];

      // Exact barcode match (highest priority)
      if (product.barcode === invoiceItem.barcode_upc) {
        score += 100;
        matchReasons.push("Exact barcode match");
      }

      // Name similarity
      const productName = product.name.toLowerCase();
      const invoiceName = invoiceItem.product_name.toLowerCase();
      
      // Exact name match
      if (productName === invoiceName) {
        score += 90;
        matchReasons.push("Exact name match");
      } else {
        // Fuzzy name matching
        const words = invoiceName.split(' ');
        const matchingWords = words.filter(word => 
          word.length > 2 && productName.includes(word)
        );
        
        if (matchingWords.length > 0) {
          const wordScore = (matchingWords.length / words.length) * 50;
          score += wordScore;
          matchReasons.push(`${matchingWords.length}/${words.length} words match`);
        }
      }

      // Brand matching
      if (invoiceItem.brand && product.name.toLowerCase().includes(invoiceItem.brand.toLowerCase())) {
        score += 30;
        matchReasons.push("Brand match");
      }

      // Search query filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!productName.includes(query) && !product.barcode.includes(query)) {
          return null; // Filter out non-matching products
        }
      }

      return {
        ...product,
        matchScore: score,
        matchReasons
      };
    })
    .filter(Boolean)
    .sort((a, b) => (b?.matchScore || 0) - (a?.matchScore || 0));

  const handleMatch = (productId: string) => {
    onMatch(invoiceItem.id, productId);
    onClose();
  };

  const getConfidenceLevel = (score: number) => {
    if (score >= 100) return { level: 'high', color: 'bg-green-100 text-green-700', label: 'High' };
    if (score >= 70) return { level: 'medium', color: 'bg-yellow-100 text-yellow-700', label: 'Medium' };
    if (score >= 40) return { level: 'low', color: 'bg-orange-100 text-orange-700', label: 'Low' };
    return { level: 'very-low', color: 'bg-red-100 text-red-700', label: 'Very Low' };
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-[600px] sm:w-[700px]">
        <SheetHeader>
          <SheetTitle>Match Invoice Item</SheetTitle>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          {/* Invoice Item Details */}
          <div className="bg-blue-50 rounded-lg p-4">
            <h3 className="font-medium text-blue-900 mb-2">Invoice Item</h3>
            <div className="text-sm text-blue-800">
              <div className="font-medium">{invoiceItem.product_name}</div>
              <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                {invoiceItem.barcode_upc && (
                  <div>Barcode: {invoiceItem.barcode_upc}</div>
                )}
                {invoiceItem.brand && (
                  <div>Brand: {invoiceItem.brand}</div>
                )}
                {invoiceItem.total_cost && (
                  <div>Cost: ${invoiceItem.total_cost.toFixed(2)}</div>
                )}
                {invoiceItem.quantity_boxes && (
                  <div>Boxes: {invoiceItem.quantity_boxes}</div>
                )}
              </div>
            </div>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Matching Products */}
          <ScrollArea className="h-96">
            <div className="space-y-3">
              {scoredProducts.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <div>No matching products found</div>
                  <div className="text-sm">All products already have costs assigned</div>
                </div>
              ) : (
                scoredProducts.map((product) => {
                  const confidence = getConfidenceLevel(product.matchScore || 0);
                  const isSelected = selectedProductId === product.id;
                  
                  return (
                    <div
                      key={product.id}
                      className={cn(
                        "border rounded-lg p-3 cursor-pointer transition-all",
                        isSelected ? "ring-2 ring-blue-500 border-blue-300" : "hover:border-gray-300"
                      )}
                      onClick={() => setSelectedProductId(product.id)}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{product.name}</div>
                          <div className="text-sm text-gray-600">{product.barcode}</div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className={cn("text-xs", confidence.color)}>
                            {confidence.label} ({product.matchScore?.toFixed(0)}%)
                          </Badge>
                          
                          {product.matchScore && product.matchScore >= 100 && (
                            <Target className="w-4 h-4 text-green-600" />
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 mb-2">
                        <div className="flex items-center gap-1">
                          <Package className="w-3 h-3" />
                          {product.boxes} × {product.unitsPerBox}
                        </div>
                        <div className="flex items-center gap-1">
                          <Hash className="w-3 h-3" />
                          {product.posStatus === 'found' ? 'In POS' : 'New'}
                        </div>
                      </div>

                      {product.matchReasons && product.matchReasons.length > 0 && (
                        <div className="mb-2">
                          <div className="text-xs text-gray-500 mb-1">Match reasons:</div>
                          <div className="flex flex-wrap gap-1">
                            {product.matchReasons.map((reason, index) => (
                              <Badge key={index} variant="secondary" className="text-xs">
                                {reason}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMatch(product.id);
                          }}
                          className="flex-1"
                          variant={product.matchScore && product.matchScore >= 100 ? "default" : "outline"}
                        >
                          {product.matchScore && product.matchScore >= 100 && (
                            <Zap className="w-3 h-3 mr-1" />
                          )}
                          Match Product
                        </Button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </ScrollArea>

          <Separator />

          {/* Actions */}
          <div className="flex gap-2">
            {onCreateNewProduct && (
              <Button
                variant="outline"
                onClick={() => {
                  onCreateNewProduct(invoiceItem);
                  onClose();
                }}
                className="flex-1"
              >
                Create New Product
              </Button>
            )}
            
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};